'use client';

import React, { useState } from 'react';
import { Input } from '../../ui/input';
import { AuthModalFooter, RegisterPrompt } from '../common/AuthModalFooter';
import { ModalCloseButton } from '../../common/modal-close-button';
import { DialogTitle } from '../../ui/dialog';
import { requestPasswordReset } from '@/lib/auth/common/client';
import { sanitizeEmail } from '@/utils/sanitize-input';
import { isValidEmail, containsXss } from '@/utils/validate-input';
import MaterialIcons from '@/components/MaterialIcons/materialIcons';

interface ResetPasswordModalProps {
  onClose?: () => void;
}

/**
 * Rendered only while open (gated behind `showResetPassword`), so fields reset by
 * unmounting — no `isOpen` prop/early-return needed.
 */
export function ResetPasswordModal({ onClose }: ResetPasswordModalProps) {
  const [email, setEmail] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [response, setResponse] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleResetPassword = async () => {
    setErrorMsg('');
    setResponse('');

    if (!email) {
      setErrorMsg('Email is required');
      return;
    }

    if (!isValidEmail(email)) {
      setErrorMsg('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    try {
      await requestPasswordReset(email);
      // Always 200, regardless of whether the account exists — nothing to branch on.
      setResponse('Email sent successfully!');
    } catch {
      // Only network-level failures land here; HTTP errors resolve instead (see lib/auth/common/client.ts).
      setErrorMsg('Failed to send reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-modal-container">
      <ModalCloseButton onClick={onClose} />
      <div className="login-modal-content">
        <DialogTitle className="cs-h3 tab:h1-compact mb-2">Forgot your password?</DialogTitle>
        <p className="cs-s-body-light text-text-default-medium mb-7.5">
          Enter your registered email address and we'll send you instructions to reset your password.
        </p>

        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <label
              htmlFor="reset-email"
              className="cs-input-field flex items-center justify-between"
            >
              <span>Email</span>
              <span className="cs-asterisk">*</span>
            </label>
            <Input
              id="reset-email"
              type="email"
              value={email}
              onChange={(e) => {
                // Sanitize + lowercase, and reject anything XSS-suspicious
                const sanitized = sanitizeEmail(e.target.value.toLowerCase());
                if (!containsXss(sanitized)) {
                  setEmail(sanitized);
                }
                setErrorMsg('');
              }}
              onFocus={() => setErrorMsg('')}
              onKeyUp={(e) => {
                if (e.key === 'Enter') {
                  handleResetPassword();
                }
              }}
              className={`min-h-[40px] !bg-surface-3 touch-manipulation ${
                errorMsg ? 'border-destructive cs-input-ring-error' : ''
              }`}
            />
          </div>

          <RegisterPrompt />

          <AuthModalFooter
            onCancel={onClose}
            onSubmit={handleResetPassword}
            loading={isLoading}
            submitLabel="Send Instructions"
            loadingLabel="Sending..."
            submitIcon={
              <MaterialIcons iconName="send" className="!text-[20px] flex-shrink-0 leading-none transition-transform duration-300 ease-in-out group-hover:translate-x-1" />
            }
            error={errorMsg}
          />

          {response && (
            <div className="text-center">
              <span className="cs-s-body-regular text-[var(--color-green-accent)]">{response}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

